import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Me } from "../types/models/me";
import { useKmsService } from "./kms-service-provider";
import { useAuth } from "./keycloak-service-provider";

type User = {
  idKeycloak: string;
  idContact: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  entreprise?: {
    id: number;
    nom: string;
    type: number;
  };
  client?: {
    id: number;
    idMinimalService: number;
    ristourne: number;
    idEntreprise: number | null;
    nomEntreprise: string | null;
    factureAutorisee: boolean;
  };
  chauffeur?: {
    id: number;
    idEntreprise: number;
    nomEntreprise: string | null;
  };
  role: string[];
  picture: string;
};

type UserContextType = {
  me: User | null;
  isLoading: boolean;
  logout: () => void;
  reload: () => void;
};

const UserContext = createContext<UserContextType | null>(null);

export const UserProvider = ({ children }: { children?: ReactNode }) => {
  const { authenticated, user, logout } = useAuth();
  const { userService } = useKmsService();

  const [isLoading, setIsLoading] = useState(true);
  const [me, setMe] = useState<User | null>(null);

  const init = useCallback(async () => {
    try {
      if (authenticated) {
        const data: Me = await userService.me();

        const rolesList: string[] = user?.realm_access?.roles ?? [];

        setMe({
          idKeycloak: user.sub,
          idContact: data.id,
          nom: data.nom,
          prenom: data.prenom,
          email: data.email,
          telephone: data.telephone,
          entreprise: data.entrepriseAdministrateur,
          client: data.client,
          chauffeur: data.chauffeur,
          picture: user.picture,
          role: rolesList,
        });
      } else {
        setMe(null);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      setMe(null);
    } finally {
      setIsLoading(false);
    }
  }, [authenticated, user]);

  useEffect(() => {
    init();
  }, [init]);

  return (
    <UserContext.Provider value={{ me, isLoading, logout, reload: init }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const services = useContext(UserContext);

  if (!services) {
    throw new Error("useIoService must be used within ServiceElectronProvider");
  }

  return services;
};
